/* ****************************************************** Inspection Time list */
export interface InspectionTimeList {
    id: number;
    name: string;
    start_time: string;
    end_time: string;
    status: 1 | 0;
}

// Single object mapping
export const mapInspectionTimeList = (data: any): InspectionTimeList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: InspectionTimeList = {
        id: data?.id,
        name: data?.name,
        start_time: data?.start_time,
        end_time: data?.end_time,
        status: data?.status,
    };

    return response;
};

// List mapping
export const mapInspectionTimeLists = (data: any[]): InspectionTimeList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapInspectionTimeList);
};
/* ****************************************************************** */


/* ****************************************************** Inspection reference data */
export interface InspectionReference {
    id: number;
    inspection_id: number | null;
    inspection_reference: string;
    status: 1 | 0;
    is_used: 1 | 0;
}

// Single object mapping
export const mapInspectionReference = (data: any): InspectionReference => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: InspectionReference = {
        id: data?.id,
        inspection_id: data?.inspection_id || null,
        inspection_reference: data?.inspection_reference,
        status: data?.status,
        is_used: data?.is_used,
    };

    return response;
};

// List mapping
export const mapInspectionReferences = (data: any[]): InspectionReference[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapInspectionReference);
};
/* ****************************************************************** */


/* ****************************************************** Inspection list data */
export interface InspectionList {
    id: number | null;
    inspection_name: string;
    status: 1 | 0;
    reference: InspectionReference[];
}

// Single object mapping
export const mapInspectionList = (data: any): InspectionList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: InspectionList = {
        id: data?.id,
        inspection_name: data?.inspection_name,
        status: data?.status,
        reference: Array.isArray(data?.reference)
            ? mapInspectionReferences(data?.reference)
            : [],
    };

    return response;
};

// List mapping
export const mapInspectionLists = (data: any[]): InspectionList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapInspectionList);
};
/* ****************************************************************** */


/* ****************************************************** Building inspector data */
export interface SiteVisitBiData {
    name: string | null;
    company_name: string | null;
}

// Single object mapping
export const mapSiteVisitBiData = (data: any): SiteVisitBiData => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitBiData = {
        name: data?.name || null,
        company_name: data?.company_name || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Architect data */
export interface SiteVisitArcData {
    name: string | null;
    company_name: string | null;
}

// Single object mapping
export const mapSiteVisitArcData = (data: any): SiteVisitArcData => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitArcData = {
        name: data?.name || null,
        company_name: data?.company_name || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Project data */
export interface SiteVisitProjectData {
    address: string | null;
    description: string | null;
}

// Single object mapping
export const mapSiteVisitProjectData = (data: any): SiteVisitProjectData => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitProjectData = {
        address: data?.address || null,
        description: data?.description || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Response file list data */
export interface ResponseFileList {
    id: number;
    response_id: number;
    path: string | null;
    extension: string;
}

// Single object mapping
export const mapResponseFileList = (data: any): ResponseFileList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: ResponseFileList = {
        id: data?.id,
        response_id: data?.response_id,
        path: data?.path || null,
        extension: data?.extension,
    };

    return response;
};

// List mapping
export const mapResponseFileLists = (data: any[]): ResponseFileList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapResponseFileList);
};
/* ****************************************************************** */


/* ****************************************************** Site visit architect response data */
export interface SiteVisitArchitectResponse {
    id: number;
    site_visit_id: number;
    note_id: number;
    date: string;
    response: string | null;
    file_list: ResponseFileList[];
}

// Single object mapping
export const mapSiteVisitArchitectResponse = (
    data: any
): SiteVisitArchitectResponse => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitArchitectResponse = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        note_id: data?.note_id,
        date: data?.date,
        response: data?.response || null,
        file_list: Array.isArray(data?.file_list)
            ? mapResponseFileLists(data?.file_list)
            : [],
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Site visit building inspector response data */
export interface SiteVisitBuildingInspectorResponse {
    id: number;
    site_visit_id: number;
    note_id: number;
    date: string;
    response: string | null;
    file_list: ResponseFileList[];
}

// Single object mapping
export const mapSiteVisitBuildingInspectorResponse = (
    data: any
): SiteVisitBuildingInspectorResponse => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitBuildingInspectorResponse = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        note_id: data?.note_id,
        date: data?.date,
        response: data?.response || null,
        file_list: Array.isArray(data?.file_list)
            ? mapResponseFileLists(data?.file_list)
            : [],
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Site visit reference data */
export interface SiteVisitReference {
    id: number;
    inspection_id: number;
    inspection_reference: string;
    status: 1 | 0;
}

// Single object mapping
export const mapSiteVisitReference = (data: any): SiteVisitReference => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitReference = {
        id: data?.id,
        inspection_id: data?.inspection_id,
        inspection_reference: data?.inspection_reference,
        status: data?.status,
    };

    return response;
};

// List mapping
export const mapSiteVisitReferences = (data: any[]): SiteVisitReference[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapSiteVisitReference);
};
/* ****************************************************************** */


/* ****************************************************** Site visit inspection data */
export interface SiteVisitInspection {
    id: number;
    inspection_name: string;
    status: 1 | 0;
    reference: SiteVisitReference[];
}

// Single object mapping
export const mapSiteVisitInspection = (data: any): SiteVisitInspection => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitInspection = {
        id: data?.id,
        inspection_name: data?.inspection_name,
        status: data?.status,
        reference: Array.isArray(data?.reference)
            ? mapSiteVisitReferences(data?.reference)
            : [],
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Site visit note list data */
export interface SiteVisitNoteList {
    id: number;
    folder_id: number;
    reference_id: number | null;
    note_no: number;
    inspection_list_id: number;
    note: string;
    is_current_site_visit_note: 1 | 0;
    parent_site_visit_no: number | null;
    status: "Satisfactory" | "Unsatisfactory";
    response_architect: SiteVisitArchitectResponse | null;
    response_building_inspector: SiteVisitBuildingInspectorResponse | null;
}

// Single object mapping
export const mapSiteVisitNoteList = (data: any): SiteVisitNoteList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitNoteList = {
        id: data?.id,
        folder_id: data?.folder_id,
        reference_id: data?.reference_id || null,
        note_no: data?.note_no,
        inspection_list_id: data?.inspection_list_id,
        note: data?.note,
        is_current_site_visit_note: data?.is_current_site_visit_note,
        parent_site_visit_no: data?.parent_site_visit_no || null,
        status: data?.status,
        response_architect: data?.response_architect
            ? mapSiteVisitArchitectResponse(data?.response_architect)
            : null,
        response_building_inspector: data?.response_building_inspector
            ? mapSiteVisitBuildingInspectorResponse(data?.response_building_inspector)
            : null,
    };

    return response;
};

// List mapping
export const mapSiteVisitNoteLists = (data: any[]): SiteVisitNoteList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapSiteVisitNoteList);
};
/* ****************************************************************** */


/* ****************************************************** This site visit inspection list data */
export interface ThisSiteVisitInspectionList {
    id: number;
    site_visit_id: number;
    type: "Default" | "Custom";
    inspection_id: number | null;
    inspection_name: string;
    site_visit_inspection: SiteVisitInspection | null;
}

// Single object mapping
export const mapThisSiteVisitInspectionList = (
    data: any
): ThisSiteVisitInspectionList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: ThisSiteVisitInspectionList = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        type: data?.type,
        inspection_id: data?.inspection_id || null,
        inspection_name: data?.inspection_name,
        site_visit_inspection: data?.site_visit_inspection
            ? mapSiteVisitInspection(data?.site_visit_inspection)
            : null,
    };

    return response;
};

// List mapping
export const mapThisSiteVisitInspectionLists = (
    data: any[]
): ThisSiteVisitInspectionList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapThisSiteVisitInspectionList);
};
/* ****************************************************************** */


/* ****************************************************** Site visit inspection list data */
export interface SiteVisitInspectionList {
    id: number;
    site_visit_id: number;
    type: "Default" | "Custom";
    inspection_id: number | null;
    inspection_name: string;
    note_list: SiteVisitNoteList[];
    site_visit_inspection: SiteVisitInspection | null;
    this_site_visit_inspection: number | null;
}

// Single object mapping
export const mapSiteVisitInspectionList = (
    data: any
): SiteVisitInspectionList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisitInspectionList = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        type: data?.type,
        inspection_id: data?.inspection_id || null,
        inspection_name: data?.inspection_name,
        note_list: Array.isArray(data?.note_list)
            ? mapSiteVisitNoteLists(data?.note_list)
            : [],
        site_visit_inspection: data?.site_visit_inspection
            ? mapSiteVisitInspection(data?.site_visit_inspection)
            : null,
        this_site_visit_inspection: data?.this_site_visit_inspection || null,
    };

    return response;
};

// List mapping
export const mapSiteVisitInspectionLists = (
    data: any[]
): SiteVisitInspectionList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapSiteVisitInspectionList);
};
/* ****************************************************************** */


/* ****************************************************** Site visit */
export interface SiteVisit {
    id: number;
    project_id: number;
    folder_id: number;
    site_visit_no: number;
    date: string;
    note: string | null;
    status: "Draft" | "Pending" | "Accepted" | "Incomplete" | "Complete";
    bi_date: string | null;
    time_id: number | null;
    start_time: string | null;
    end_time: string | null;
    generate_report: 1 | 0;
    sign_report: 1 | 0;
    sign: string | null;
    sign_date: string | null;
    file_download_path: string | null;
    client_name: string | null;
    building_inspector_data: SiteVisitBiData | null;
    architecture_data: SiteVisitArcData | null;
    project_data: SiteVisitProjectData | null;
    this_site_visit_inspection_list: ThisSiteVisitInspectionList[];
    siteVisitInspectionList: SiteVisitInspectionList[];
}

// Single object mapping
export const mapSiteVisitList = (data: any): SiteVisit => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: SiteVisit = {
        id: data?.id,
        project_id: data?.project_id,
        folder_id: data?.folder_id,
        site_visit_no: data?.site_visit_no,
        date: data?.date,
        note: data?.note,
        status: data?.status,
        bi_date: data?.bi_date || null,
        time_id: data?.time_id || null,
        start_time: data?.start_time || null,
        end_time: data?.end_time || null,
        generate_report: data?.generate_report,
        sign_report: data?.sign_report,
        sign: data?.sign || null,
        sign_date: data?.sign_date || null,
        file_download_path: data?.file_download_path || null,
        client_name: data?.client_name || null,
        building_inspector_data: data?.building_inspector_data
            ? mapSiteVisitBiData(data?.building_inspector_data)
            : null,
        architecture_data: data?.architecture_data
            ? mapSiteVisitArcData(data?.architecture_data)
            : null,
        project_data: data?.project_data
            ? mapSiteVisitProjectData(data?.project_data)
            : null,
        this_site_visit_inspection_list: Array.isArray(
            data?.this_site_visit_inspection_list
        )
            ? mapThisSiteVisitInspectionLists(data?.this_site_visit_inspection_list)
            : [],
        siteVisitInspectionList: Array.isArray(data?.siteVisitInspectionList)
            ? mapSiteVisitInspectionLists(data?.siteVisitInspectionList)
            : [],
    };

    return response;
};

// List mapping
export const mapSiteVisitLists = (data: any[]): SiteVisit[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapSiteVisitList);
};
/* ****************************************************************** */


/* For Api responce */
export interface PostInspectionList {
    type: 'Default' | 'Custom';
    id: number | null;
    inspection_name: string;
}

// Single object mapping
export const mapPostInspectionList = (data: any): PostInspectionList => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const response: PostInspectionList = {
        type: data?.type,
        id: data?.id || null,
        inspection_name: data?.inspection_name,
    }

    return response;
};

// List mapping
export const mapPostInspectionListLists = (data: any[]): PostInspectionList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapPostInspectionList);
};